"use client";

import { useCallback, useMemo, useState } from "react";
import { BookOpen, ChevronDown } from "lucide-react";

export type LevelModules = Array<{ level: string; modules: string[] }>;

export function CourseContentSection({ levelModules }: { levelModules?: LevelModules }) {
  const curriculum = levelModules?.length ? levelModules : null;

  const headline = useMemo(() => {
    if (!curriculum) return "Course content will appear here.";
    const levelCount = curriculum.length;
    const moduleCount = curriculum.reduce((acc, l) => acc + l.modules.length, 0);
    return `${levelCount} levels • ${moduleCount} modules`;
  }, [curriculum]);

  const [open, setOpen] = useState<Set<string>>(
    () => new Set(curriculum ? [curriculum[0]!.level] : []),
  );

  const allIds = useMemo(() => new Set((curriculum ?? []).map((s) => s.level)), [curriculum]);

  const expandAll = useCallback(() => {
    setOpen(new Set(allIds));
  }, [allIds]);

  const toggle = useCallback((id: string) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  return (
    <div className="mt-8">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-lg font-black text-slate-900 md:text-xl">Course content</h2>
          <p className="mt-1 text-sm text-slate-500">{headline}</p>
        </div>
        {curriculum ? (
          <button
            type="button"
            onClick={expandAll}
            className="self-start text-sm font-semibold text-violet-700 underline-offset-2 hover:underline sm:self-auto"
          >
            Expand all levels
          </button>
        ) : null}
      </div>

      <div className="mt-4 overflow-hidden rounded-xl border border-slate-200 bg-white">
        {curriculum?.map((section) => {
          const isOpen = open.has(section.level);
          const summary = `${section.modules.length} modules`;
          return (
            <div key={section.level} className="border-b border-slate-200 last:border-b-0">
              <button
                type="button"
                onClick={() => toggle(section.level)}
                className="flex w-full items-center gap-2 bg-slate-50/90 px-4 py-3.5 text-left transition-colors hover:bg-slate-100/90 md:px-5"
                aria-expanded={isOpen}
              >
                <ChevronDown
                  className={`h-4 w-4 shrink-0 text-slate-600 transition-transform ${isOpen ? "rotate-180" : ""}`}
                  aria-hidden
                />
                <span className="min-w-0 flex-1 text-sm font-bold text-slate-900 md:text-base">
                  {section.level}
                </span>
                <span className="shrink-0 text-xs text-slate-500 tabular-nums md:text-sm">{summary}</span>
              </button>

              {isOpen ? (
                <ul className="divide-y divide-slate-200 bg-white">
                  {section.modules.map((title, idx) => (
                    <li
                      key={`${section.level}:${idx}`}
                      className="flex items-start gap-3 px-4 py-3.5 text-sm text-slate-800 md:px-5 md:py-3"
                    >
                      <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                        <BookOpen className="h-4 w-4" aria-hidden />
                      </span>
                      <span className="min-w-0 flex-1 leading-relaxed font-semibold text-slate-800">
                        Module {idx + 1}: {title}
                      </span>
                      <span className="sr-only">Locked</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          );
        })}
        {!curriculum ? (
          <div className="p-5 text-sm text-slate-600">
            Content details are coming soon.
          </div>
        ) : null}
      </div>
    </div>
  );
}
